import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAppContext } from 'hooks';
import {
  hasRedirectPathInQuery,
  isSignUpV2ModalOpen,
} from 'helpers/signUpModals';

/**
 * Listens for the URL params and AppContext state changes that cause an auth
 * modal to open and flips the matching visibility flag. Always mounted in
 * `<Header />`, which lets the heavy `SignIn` / `SignUp` / `ResetPassword`
 * components stay conditionally mounted (and therefore code-split off the
 * critical path for anonymous viewers) without losing the deep-link /
 * email-link / social-OAuth entry points.
 *
 * Idempotent: setting a visibility flag that is already true is a no-op, so
 * `useRouter().query` getting a new object reference on every router event
 * does not re-open a modal the user has explicitly closed (the visibility
 * flag stays true while their URL still contains the param; the user clicks
 * close → flag flips false → the modal is the one that scrubs the query
 * param via `removeSignUpQueryParam`, so a subsequent re-render no longer
 * sees the trigger).
 */
const AuthModalQueryWatcher = (): null => {
  const { query } = useRouter();
  const {
    setLoginModalVisibility,
    setSignUpModalVisibility,
    setResetPasswordModalVisibility,
    token,
    isSocialSignUp,
    socialSignUpData,
  } = useAppContext();

  // `?signIn=...` opens the sign-in modal.
  useEffect(() => {
    if (query.signIn) {
      setLoginModalVisibility(true);
    }
  }, [query.signIn, setLoginModalVisibility]);

  // Deep links with `redirectPath` open the sign-in modal for guests so they
  // can authenticate and continue to the target. No-op for logged-in users.
  useEffect(() => {
    if (hasRedirectPathInQuery(query) && !token?.accessToken) {
      setLoginModalVisibility(true);
    }
  }, [query, token?.accessToken, setLoginModalVisibility]);

  // `?sign-up=` / `?signUp=` from marketing/email links opens the sign-up
  // modal for guests. Mirrors the in-component effect at
  // SignUpV2.tsx:334-339 so it works even when SignUp isn't mounted yet.
  useEffect(() => {
    if (isSignUpV2ModalOpen(query) && !token?.accessToken) {
      setSignUpModalVisibility(true);
    }
  }, [query, token?.accessToken, setSignUpModalVisibility]);

  // Social OAuth (Google / Facebook / Twitter / TikTok) sets these flags in
  // AppContext when the provider redirect completes. The in-component
  // effect in SignUpV2 also handles form pre-fill + step bump once the modal
  // mounts; we just need to flip visibility from outside so the component
  // can mount in the first place.
  useEffect(() => {
    if (isSocialSignUp && socialSignUpData) {
      setSignUpModalVisibility(true);
    }
  }, [isSocialSignUp, socialSignUpData, setSignUpModalVisibility]);

  // Password-reset emails arrive with `?passwordResetToken=...`.
  useEffect(() => {
    if (query.passwordResetToken) {
      setResetPasswordModalVisibility(true);
    }
  }, [query.passwordResetToken, setResetPasswordModalVisibility]);

  return null;
};

export default AuthModalQueryWatcher;
